import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart3,
  Bot,
  Calendar,
  CheckCircle2,
  Clock,
  Columns3,
  Download,
  FileSpreadsheet,
  Link2,
  Loader2,
  Lock,
  MessageCircle,
  Plus,
  Sparkles,
  TrendingUp,
  Video,
  Workflow,
  Zap,
} from 'lucide-react';
import Button from '../../components/shared/Button';
import Input from '../../components/shared/Input';
import CustomSelect from '../../components/shared/CustomSelect';
import WorkspaceBoard from '../../components/vendor/workspace/WorkspaceBoard';
import AskAgentModal from '../../components/vendor/workspace/AskAgentModal';
import WhatsAppDraftModal from '../../components/vendor/workspace/WhatsAppDraftModal';
import { VendorPageHeader, VendorGuideCallout } from '../../components/vendor/guidance';
import { VENDOR_PAGE_GUIDES } from '../../utils/vendorPageGuides';
import { useToastContext } from '../../contexts/ToastContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import useVendorPlanAccess from '../../hooks/useVendorPlanAccess';
import {
  useCreateWorkspaceItem,
  useDeleteWorkspaceItem,
  useMoveWorkspaceItem,
  useVendorWorkspaceInsights,
  useVendorWorkspaceLinkOptions,
  useVendorWorkspaceToday,
  vendorWorkspaceApi,
} from '../../services/vendorWorkspaceService';
import { formatDisplayMoney } from '../../utils/formatMoney';

const TYPE_OPTIONS = [
  { value: 'todo', label: 'To-do' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'schedule', label: 'Schedule block' },
  { value: 'reminder', label: 'Reminder' },
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

const CHANNEL_OPTIONS = [
  { value: '', label: 'Both channels' },
  { value: 'internal', label: 'Internal' },
  { value: 'external', label: 'External' },
];

const emptyDraft = () => ({
  type: 'todo',
  title: '',
  description: '',
  priority: 'normal',
  channel: '',
  due_at: '',
  starts_at: '',
  ends_at: '',
  meet_link: '',
  link_ref: '',
});

const PAGE_TABS = [
  { id: 'board', name: 'Board', icon: Columns3 },
  { id: 'today', name: 'Today', icon: Calendar },
  { id: 'insights', name: 'Insights', icon: BarChart3 },
  { id: 'tools', name: 'Tools', icon: Sparkles },
];

const TYPE_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'todo', label: 'To-dos' },
  { id: 'meeting', label: 'Meetings' },
  { id: 'schedule', label: 'Schedule' },
];

const STATE_FILTERS = [
  { id: null, label: 'All states' },
  { id: 'open', label: 'To do' },
  { id: 'in_progress', label: 'Doing' },
  { id: 'done', label: 'Done' },
];

const VendorWorkspace = () => {
  const { success, error: showError } = useToastContext();
  const { confirm } = useConfirm();
  const { isFreeTier, currentPlanSlug } = useVendorPlanAccess();
  const [pageTab, setPageTab] = useState('board'); // board | today | insights | tools
  const [typeTab, setTypeTab] = useState('all'); // all | todo | meeting | schedule
  const [stateFilter, setStateFilter] = useState(null); // open | in_progress | done | null
  const [draft, setDraft] = useState(emptyDraft());
  const [showComposer, setShowComposer] = useState(false);
  const [showAskAgent, setShowAskAgent] = useState(false);
  const [agentPreset, setAgentPreset] = useState(null);
  const [waItem, setWaItem] = useState(null);
  const [exporting, setExporting] = useState(false);

  const { data: todayRes, isLoading } = useVendorWorkspaceToday();
  const payload = todayRes?.data || {};
  const summary = payload.summary || {};
  const items = payload.items || [];
  const bookings = payload.bookings || [];
  const plan = payload.plan || {};
  const caps = plan.capabilities || {};
  const usage = plan.usage || {};
  const tierLabel = caps.tier_label || (isFreeTier ? 'Free' : currentPlanSlug || 'Plan');

  const { data: insightsRes, isLoading: insightsLoading } = useVendorWorkspaceInsights(
    { range: 30 },
    { enabled: true }
  );
  const insights = insightsRes?.data || {};
  const pulse = insights.pulse || {};
  const extended = insights.extended || null;
  const roi = insights.roi || {};
  const funnel = insights.funnel || null;
  const forecast = insights.forecast || null;
  const biUnlocked = Boolean(insights.bi_unlocked ?? caps.can_bi_insights);
  const forecastUnlocked = Boolean(insights.forecast_unlocked ?? caps.can_automations);

  const { data: linkRes } = useVendorWorkspaceLinkOptions({
    enabled: caps.can_link_entities === true && showComposer,
  });
  const linkOptions = useMemo(() => {
    const raw = linkRes?.data?.options || [];
    return raw.map((o) => ({ value: o.value || '', label: o.label || 'No link' }));
  }, [linkRes]);

  const createMutation = useCreateWorkspaceItem({
    onSuccess: () => {
      success('Added to workspace', 'Your item is on the board.');
      setDraft(emptyDraft());
      setShowComposer(false);
    },
    onError: (err) => {
      showError(
        err?.response?.data?.message || 'Could not create item',
        err?.response?.data?.upgrade_required
          ? 'Upgrade your plan to unlock this workspace feature.'
          : undefined
      );
    },
  });

  const moveMutation = useMoveWorkspaceItem({
    onError: (err) => showError(null, err?.response?.data?.message || 'Could not move item'),
  });

  const deleteMutation = useDeleteWorkspaceItem({
    onSuccess: () => success(null, 'Removed.'),
    onError: (err) => showError(null, err?.response?.data?.message || 'Could not delete item'),
  });

  const typeFilter = useMemo(() => {
    if (typeTab === 'todo' || typeTab === 'meeting' || typeTab === 'schedule') return typeTab;
    return null;
  }, [typeTab]);

  const allowedTypes = useMemo(() => {
    return TYPE_OPTIONS.filter((opt) => {
      if (opt.value === 'meeting') return caps.can_meetings !== false;
      if (opt.value === 'schedule') return caps.can_schedule_blocks === true;
      return true;
    });
  }, [caps]);

  const submitDraft = (e) => {
    e.preventDefault();
    if (!draft.title.trim()) {
      showError('Title required', 'Give this item a short name.');
      return;
    }
    createMutation.mutate({
      type: draft.type,
      title: draft.title.trim(),
      description: draft.description.trim() || null,
      priority: draft.priority,
      channel: draft.channel || null,
      due_at: draft.due_at || null,
      starts_at: draft.starts_at || null,
      ends_at: draft.ends_at || null,
      meet_link: draft.meet_link.trim() || null,
      link_ref: draft.link_ref || null,
    });
  };

  const handleExportCsv = async () => {
    if (!caps.can_excel_export) {
      showError('Upgrade required', 'CSV export is available on Basic and higher.');
      return;
    }
    setExporting(true);
    try {
      const res = await vendorWorkspaceApi.exportCsv({ open_only: 0 });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `workspace-export-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      success(null, 'Workspace CSV downloaded.');
    } catch (err) {
      showError(
        err?.response?.data?.message || 'Export failed',
        err?.response?.data?.upgrade_required ? 'Upgrade to Basic+ for Excel/CSV export.' : undefined
      );
    } finally {
      setExporting(false);
    }
  };

  const powerCards = [
    {
      key: 'automations',
      title: 'Process automation',
      body: 'Recipes that create board tasks from leads, invoices, and payments.',
      Icon: Workflow,
      unlocked: caps.can_automations,
      to: '/vendor/automations',
    },
    {
      key: 'whatsapp',
      title: 'WhatsApp integration',
      body: 'Connect messaging from Automations when your plan allows.',
      Icon: MessageCircle,
      unlocked: caps.can_whatsapp,
      to: '/vendor/automations',
    },
    {
      key: 'excel',
      title: 'Excel / Sheets sync',
      body: 'CSV on Basic+; live Google Sheet push on Pro (Integrations).',
      Icon: FileSpreadsheet,
      unlocked: caps.can_sheets_live || caps.can_excel_export,
      to: '/vendor/integrations',
    },
    {
      key: 'agents',
      title: 'Workflow agents',
      body: 'AI agents that draft agendas, chase balances, and prep event packs.',
      Icon: Bot,
      unlocked: caps.can_agents,
      to: '/vendor/automations',
    },
  ];

  const openComposer = () => {
    setPageTab('board');
    setShowComposer(true);
  };

  return (
    <div className="space-y-6 pb-12">
      <VendorPageHeader
        title="Workspace"
        subtitle="Drag cards across your board — to-dos, meetings, and today’s jobs across Internal and External."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-ink-secondary">
              <Sparkles className="h-3.5 w-3.5" />
              {tierLabel}
              {caps.max_open_items != null
                ? ` · ${usage.open_items ?? 0}/${caps.max_open_items} open`
                : ' · Unlimited'}
            </span>
            <Button
              size="sm"
              variant="outline"
              className="rounded-md"
              onClick={handleExportCsv}
              loading={exporting}
              icon={caps.can_excel_export ? Download : Lock}
              title={caps.can_excel_export ? 'Download CSV' : 'Basic+ required'}
            >
              Export CSV
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="rounded-md"
              onClick={() => {
                setAgentPreset(null);
                setShowAskAgent(true);
              }}
              icon={caps.can_agents ? Bot : Lock}
              title={caps.can_agents ? 'Ask a workflow agent' : 'Agents require Pro'}
            >
              Ask agent
            </Button>
            <Button size="sm" className="rounded-md" onClick={openComposer} icon={Plus}>
              Add item
            </Button>
          </div>
        }
      />

      <VendorGuideCallout
        title={VENDOR_PAGE_GUIDES.workspace.title}
        steps={VENDOR_PAGE_GUIDES.workspace.steps}
        icon={Zap}
      />

      <AskAgentModal
        isOpen={showAskAgent}
        onClose={() => {
          setShowAskAgent(false);
          setAgentPreset(null);
        }}
        canAgents={Boolean(caps.can_agents)}
        presetEntity={agentPreset}
      />
      <WhatsAppDraftModal
        isOpen={Boolean(waItem)}
        onClose={() => setWaItem(null)}
        item={waItem}
        canWhatsapp={Boolean(caps.can_whatsapp)}
      />

      <div className="product-panel overflow-hidden">
        <div className="overflow-x-auto border-b border-border px-4 py-3 sm:px-5">
          <div
            className="inline-flex min-w-max flex-wrap rounded-xl border border-border bg-gray-100 p-1"
            role="tablist"
            aria-label="Workspace sections"
          >
            {PAGE_TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = pageTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setPageTab(tab.id)}
                  className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-2 text-xs font-medium transition-all sm:gap-2 sm:px-3.5 sm:text-sm ${
                    isActive
                      ? 'bg-surface text-ink shadow-sm'
                      : 'text-ink-secondary hover:text-gray-900'
                  }`}
                >
                  <Icon className="h-4 w-4 flex-shrink-0" />
                  <span>{tab.name}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="space-y-5 p-4 sm:p-6 lg:p-8">
          {pageTab === 'board' && (
            <>
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
                {[
                  { label: 'Open todos', value: summary.open_todos ?? 0, Icon: CheckCircle2 },
                  { label: 'Meetings today', value: summary.meetings_today ?? 0, Icon: Video },
                  { label: 'Bookings today', value: summary.bookings_today ?? 0, Icon: Calendar },
                  { label: 'Overdue', value: summary.overdue ?? 0, Icon: Clock },
                  { label: 'Schedule blocks', value: summary.schedule_blocks ?? 0, Icon: Link2 },
                ].map(({ label, value, Icon }) => (
                  <div
                    key={label}
                    className="rounded-product-md border border-border bg-surface p-3 sm:p-4"
                  >
                    <div className="mb-1 flex items-center gap-1.5 text-ink-tertiary">
                      <Icon className="h-3.5 w-3.5" />
                      <p className="text-[10px] font-semibold uppercase tracking-[0.12em]">{label}</p>
                    </div>
                    {isLoading ? (
                      <Loader2 className="h-5 w-5 animate-spin text-ink-tertiary" />
                    ) : (
                      <p className="text-2xl font-semibold tabular-nums text-ink">{value}</p>
                    )}
                  </div>
                ))}
              </div>

              {showComposer && (
                <form
                  onSubmit={submitDraft}
                  className="space-y-4 rounded-product-md border border-border bg-surface p-4 sm:p-5"
                >
                  <div className="flex items-center justify-between gap-3">
                    <h2 className="text-sm font-semibold text-ink">Quick add</h2>
                    <button
                      type="button"
                      className="text-xs font-medium text-ink-secondary hover:text-ink"
                      onClick={() => setShowComposer(false)}
                    >
                      Close
                    </button>
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <CustomSelect
                      label="Type"
                      options={allowedTypes}
                      value={draft.type}
                      onChange={(v) => setDraft((p) => ({ ...p, type: v }))}
                    />
                    <CustomSelect
                      label="Priority"
                      options={PRIORITY_OPTIONS}
                      value={draft.priority}
                      onChange={(v) => setDraft((p) => ({ ...p, priority: v }))}
                    />
                    <CustomSelect
                      label="Channel"
                      options={CHANNEL_OPTIONS}
                      value={draft.channel}
                      onChange={(v) => setDraft((p) => ({ ...p, channel: v }))}
                    />
                    <Input
                      label={draft.type === 'todo' || draft.type === 'reminder' ? 'Due' : 'Starts'}
                      type="datetime-local"
                      value={
                        draft.type === 'todo' || draft.type === 'reminder'
                          ? draft.due_at
                          : draft.starts_at
                      }
                      onChange={(e) =>
                        setDraft((p) =>
                          draft.type === 'todo' || draft.type === 'reminder'
                            ? { ...p, due_at: e.target.value }
                            : { ...p, starts_at: e.target.value }
                        )
                      }
                    />
                  </div>
                  <Input
                    label="Title *"
                    value={draft.title}
                    onChange={(e) => setDraft((p) => ({ ...p, title: e.target.value }))}
                    placeholder={
                      draft.type === 'meeting'
                        ? 'e.g. Discovery call — Smith wedding'
                        : 'e.g. Send deposit reminder'
                    }
                  />
                  {(draft.type === 'meeting' || draft.type === 'schedule') && (
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <Input
                        label="End"
                        type="datetime-local"
                        value={draft.ends_at}
                        onChange={(e) => setDraft((p) => ({ ...p, ends_at: e.target.value }))}
                      />
                      {draft.type === 'meeting' && (
                        <Input
                          label="Meet link"
                          value={draft.meet_link}
                          onChange={(e) => setDraft((p) => ({ ...p, meet_link: e.target.value }))}
                          placeholder="https://meet.google.com/…"
                        />
                      )}
                    </div>
                  )}
                  <Input
                    label="Notes (optional)"
                    value={draft.description}
                    onChange={(e) => setDraft((p) => ({ ...p, description: e.target.value }))}
                    placeholder="Context for you or your team"
                  />
                  {caps.can_link_entities ? (
                    <CustomSelect
                      label="Link to booking / lead / invoice"
                      options={linkOptions.length ? linkOptions : [{ value: '', label: 'Loading…' }]}
                      value={draft.link_ref}
                      onChange={(v) => setDraft((p) => ({ ...p, link_ref: v }))}
                      placeholder="Optional — connect this task"
                    />
                  ) : (
                    <p className="rounded-md border border-dashed border-border bg-surface-muted/40 px-3 py-2 text-xs text-ink-secondary">
                      <Lock className="mr-1 inline h-3 w-3" />
                      Link tasks to bookings, leads, and invoices on Basic+.{' '}
                      <Link
                        to="/vendor/subscription"
                        className="font-medium text-ink underline-offset-2 hover:underline"
                      >
                        Upgrade
                      </Link>
                    </p>
                  )}
                  {!caps.can_schedule_blocks && draft.type === 'schedule' && (
                    <p className="text-xs text-amber-700">
                      Schedule blocks need Basic+. Switch to To-do or Meeting, or upgrade.
                    </p>
                  )}
                  <div className="flex flex-wrap gap-2">
                    <Button type="submit" loading={createMutation.isPending} className="rounded-md">
                      Save to workspace
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="rounded-md"
                      onClick={() => setDraft(emptyDraft())}
                    >
                      Clear
                    </Button>
                  </div>
                </form>
              )}

              <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
                <div className="flex flex-wrap gap-2">
                  <span className="self-center text-[10px] font-semibold uppercase tracking-wide text-ink-tertiary">
                    Type
                  </span>
                  {TYPE_FILTERS.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setTypeTab(t.id)}
                      className={`rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${
                        typeTab === t.id
                          ? 'border-ink bg-ink text-ink-on-dark'
                          : 'border-border bg-surface text-ink-secondary hover:bg-surface-muted'
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2 sm:ml-2 sm:border-l sm:border-border sm:pl-4">
                  <span className="self-center text-[10px] font-semibold uppercase tracking-wide text-ink-tertiary">
                    State
                  </span>
                  {STATE_FILTERS.map((t) => (
                    <button
                      key={t.label}
                      type="button"
                      onClick={() => setStateFilter(t.id)}
                      className={`rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${
                        stateFilter === t.id
                          ? 'border-ink bg-ink text-ink-on-dark'
                          : 'border-border bg-surface text-ink-secondary hover:bg-surface-muted'
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
                {!showComposer && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-md sm:ml-auto"
                    onClick={() => setShowComposer(true)}
                    icon={Plus}
                  >
                    Quick add
                  </Button>
                )}
              </div>

              <section className="w-full space-y-3">
                <div className="flex flex-wrap items-end justify-between gap-2">
                  <div>
                    <h2 className="text-lg font-semibold tracking-tight text-ink">Workflow board</h2>
                    <p className="text-sm text-ink-secondary">
                      Filter by type or state, then drag cards To do → Doing → Done.
                    </p>
                  </div>
                  {items.length === 0 && (
                    <Button size="sm" className="rounded-md" onClick={openComposer} icon={Plus}>
                      Add first item
                    </Button>
                  )}
                </div>
                {isLoading ? (
                  <div className="flex min-h-[320px] w-full items-center justify-center rounded-md border border-ink bg-canvas">
                    <Loader2 className="h-7 w-7 animate-spin text-ink" />
                  </div>
                ) : (
                  <WorkspaceBoard
                    items={items}
                    typeFilter={typeFilter}
                    statusFilter={stateFilter}
                    boardDateLabel={
                      payload.date
                        ? new Date(`${payload.date}T12:00:00`).toLocaleDateString(undefined, {
                            month: 'long',
                            year: 'numeric',
                          })
                        : null
                    }
                    isMoving={moveMutation.isPending}
                    canWhatsapp={Boolean(caps.can_whatsapp)}
                    canAgents={Boolean(caps.can_agents)}
                    onWhatsApp={(item) => setWaItem(item)}
                    onRunAgent={(_item, preset) => {
                      setAgentPreset(preset);
                      setShowAskAgent(true);
                    }}
                    onMove={(payloadMove) => moveMutation.mutate(payloadMove)}
                    onDelete={async (item) => {
                      const ok = await confirm({
                        title: 'Delete workspace item?',
                        message: item?.title
                          ? `“${item.title}” will be removed from your board. This cannot be undone.`
                          : 'This item will be removed from your board. This cannot be undone.',
                        confirmLabel: 'Delete',
                        variant: 'danger',
                      });
                      if (ok) deleteMutation.mutate(item.id);
                    }}
                  />
                )}
              </section>
            </>
          )}

          {pageTab === 'today' && (
            <section className="space-y-3">
              <div>
                <h2 className="text-lg font-semibold tracking-tight text-ink">Today’s bookings</h2>
                <p className="text-sm text-ink-secondary">
                  Jobs on the calendar for today — jump into the booking or lead workspace.
                </p>
              </div>
              <div className="product-surface overflow-hidden">
                {isLoading ? (
                  <div className="flex items-center justify-center px-4 py-12">
                    <Loader2 className="h-6 w-6 animate-spin text-ink-tertiary" />
                  </div>
                ) : bookings.length === 0 ? (
                  <p className="px-4 py-8 text-center text-sm text-ink-secondary">
                    No events on the calendar for today.
                  </p>
                ) : (
                  <ul className="divide-y divide-border">
                    {bookings.map((b) => (
                      <li key={b.id}>
                        <Link
                          to={b.href || `/vendor/bookings/${b.id}`}
                          className="block px-4 py-3 transition-colors hover:bg-surface-muted"
                        >
                          <p className="text-sm font-semibold text-ink">{b.title}</p>
                          <p className="mt-0.5 text-xs text-ink-secondary">
                            {b.subtitle}
                            {b.channel ? ` · ${b.channel}` : ''}
                          </p>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <Link
                  to="/vendor/consultations"
                  className="rounded-md border border-border bg-surface px-2.5 py-1.5 text-xs font-medium text-ink hover:bg-surface-muted"
                >
                  Internal leads
                </Link>
                <Link
                  to="/vendor/external-leads"
                  className="rounded-md border border-border bg-surface px-2.5 py-1.5 text-xs font-medium text-ink hover:bg-surface-muted"
                >
                  External leads
                </Link>
                <Link
                  to="/vendor/private-bookings"
                  className="rounded-md border border-border bg-surface px-2.5 py-1.5 text-xs font-medium text-ink hover:bg-surface-muted"
                >
                  Private bookings
                </Link>
                <Link
                  to="/vendor/calendar"
                  className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-2.5 py-1.5 text-xs font-medium text-ink hover:bg-surface-muted"
                >
                  <Calendar className="h-3.5 w-3.5" />
                  Full calendar
                </Link>
              </div>
            </section>
          )}

          {pageTab === 'insights' && (
            <section className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h2 className="text-lg font-semibold tracking-tight text-ink">
                    Insights · last {pulse.range_days || 30} days
                  </h2>
                  <p className="text-sm text-ink-secondary">
                    Board pulse, funnel, forecast, and automation ROI.
                  </p>
                </div>
                {!biUnlocked && (
                  <Link
                    to="/vendor/subscription"
                    className="inline-flex items-center gap-1 text-xs font-medium text-ink-secondary hover:text-ink"
                  >
                    <Lock className="h-3 w-3" />
                    Unlock full BI on Basic+
                  </Link>
                )}
              </div>
              {insightsLoading ? (
                <Loader2 className="h-5 w-5 animate-spin text-ink-tertiary" />
              ) : (
                <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-6">
                  {[
                    { label: 'Completed', value: pulse.completed ?? 0, Icon: CheckCircle2 },
                    { label: 'Created', value: pulse.created ?? 0, Icon: Plus },
                    {
                      label: 'Completion',
                      value: `${pulse.completion_rate ?? 0}%`,
                      Icon: TrendingUp,
                    },
                    { label: 'Overdue now', value: pulse.overdue ?? 0, Icon: Clock },
                    {
                      label: 'Meetings ahead',
                      value: pulse.meetings_upcoming ?? 0,
                      Icon: Video,
                    },
                    {
                      label: 'Jobs next 7d',
                      value: pulse.bookings_next_7_days ?? 0,
                      Icon: Calendar,
                    },
                  ].map(({ label, value, Icon }) => (
                    <div
                      key={label}
                      className="rounded-md border border-border bg-surface-muted/40 px-3 py-2.5"
                    >
                      <div className="mb-0.5 flex items-center gap-1 text-ink-tertiary">
                        <Icon className="h-3 w-3" />
                        <p className="text-[10px] font-semibold uppercase tracking-wide">{label}</p>
                      </div>
                      <p className="text-lg font-semibold tabular-nums text-ink">{value}</p>
                    </div>
                  ))}
                </div>
              )}
              {biUnlocked && extended && (
                <div className="grid grid-cols-2 gap-3 border-t border-border pt-3 md:grid-cols-4">
                  <div className="text-xs text-ink-secondary">
                    Internal leads open{' '}
                    <span className="font-semibold text-ink">
                      {extended.pending_internal_leads ?? 0}
                    </span>
                  </div>
                  <div className="text-xs text-ink-secondary">
                    External leads open{' '}
                    <span className="font-semibold text-ink">
                      {extended.pending_external_leads ?? 0}
                    </span>
                  </div>
                  <div className="text-xs text-ink-secondary">
                    Open invoices{' '}
                    <span className="font-semibold text-ink">{extended.open_invoices ?? 0}</span>
                  </div>
                  <div className="text-xs text-ink-secondary">
                    External balance due{' '}
                    <span className="font-semibold text-ink">
                      {formatDisplayMoney(extended.external_balance_due ?? 0, 'USD', 'en-US')}
                    </span>
                  </div>
                </div>
              )}
              {roi &&
                (roi.automation_runs_30d > 0 || roi.agent_runs_30d > 0 || caps.can_automations) && (
                  <div className="grid grid-cols-2 gap-3 border-t border-border pt-3 md:grid-cols-5">
                    <div className="text-xs text-ink-secondary">
                      Automation runs (30d){' '}
                      <span className="font-semibold text-ink">{roi.automation_runs_30d ?? 0}</span>
                    </div>
                    <div className="text-xs text-ink-secondary">
                      Hours saved (30d){' '}
                      <span className="font-semibold text-ink">{roi.hours_saved_30d ?? 0}</span>
                    </div>
                    <div className="text-xs text-ink-secondary">
                      Agent runs (30d){' '}
                      <span className="font-semibold text-ink">{roi.agent_runs_30d ?? 0}</span>
                    </div>
                    <div className="text-xs text-ink-secondary">
                      Error rate{' '}
                      <span className="font-semibold text-ink">
                        {roi.automation_error_rate_30d ?? 0}%
                      </span>
                    </div>
                    <div className="text-xs text-ink-secondary">
                      Influenced revenue{' '}
                      <span className="font-semibold text-ink">
                        {formatDisplayMoney(roi.influenced_revenue_30d ?? 0, 'USD', 'en-US')}
                      </span>
                    </div>
                  </div>
                )}
              {biUnlocked && funnel && (
                <div className="grid grid-cols-1 gap-3 border-t border-border pt-3 md:grid-cols-2">
                  {[
                    { label: 'Internal funnel', data: funnel.internal },
                    { label: 'External funnel', data: funnel.external },
                  ].map(({ label, data }) => (
                    <div
                      key={label}
                      className="rounded-md border border-border bg-surface-muted/40 px-3 py-2.5"
                    >
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-ink-tertiary">
                        {label}
                      </p>
                      <p className="mt-1 text-xs text-ink-secondary">
                        Lead <span className="font-semibold text-ink">{data?.lead ?? 0}</span>
                        {' → '}Meeting{' '}
                        <span className="font-semibold text-ink">{data?.meeting ?? 0}</span>
                        {' → '}Booked{' '}
                        <span className="font-semibold text-ink">{data?.booked ?? 0}</span>
                      </p>
                    </div>
                  ))}
                </div>
              )}
              {forecastUnlocked && forecast && (
                <div className="grid grid-cols-2 gap-3 border-t border-border pt-3 md:grid-cols-4">
                  <div className="text-xs text-ink-secondary">
                    Forecast jobs (14d){' '}
                    <span className="font-semibold text-ink">{forecast.jobs ?? 0}</span>
                  </div>
                  <div className="text-xs text-ink-secondary">
                    Collectible{' '}
                    <span className="font-semibold text-ink">
                      {formatDisplayMoney(forecast.collectible ?? 0, 'USD', 'en-US')}
                    </span>
                  </div>
                  <div className="text-xs text-ink-secondary">
                    Already paid{' '}
                    <span className="font-semibold text-ink">
                      {formatDisplayMoney(forecast.already_paid ?? 0, 'USD', 'en-US')}
                    </span>
                  </div>
                  <div className="text-xs text-ink-secondary">
                    Remaining{' '}
                    <span className="font-semibold text-ink">
                      {formatDisplayMoney(forecast.remaining ?? 0, 'USD', 'en-US')}
                    </span>
                  </div>
                </div>
              )}
            </section>
          )}

          {pageTab === 'tools' && (
            <section className="space-y-4">
              <div>
                <h2 className="text-lg font-semibold tracking-tight text-ink">Power features</h2>
                <p className="text-sm text-ink-secondary">
                  Tiered by plan — manage recipes and connections on Automations.
                </p>
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {powerCards.map(({ key, title, body, Icon, unlocked, to }) => (
                  <Link
                    key={key}
                    to={to || '/vendor/subscription'}
                    className={`block w-full rounded-product-md border p-3 transition-colors ${
                      unlocked
                        ? 'border-border bg-surface hover:bg-surface-muted'
                        : 'border-dashed border-border bg-surface-muted/50'
                    }`}
                  >
                    <div className="mb-1 flex items-center gap-2">
                      <Icon className="h-4 w-4 text-ink-tertiary" />
                      <p className="text-sm font-semibold text-ink">{title}</p>
                      {!unlocked && (
                        <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-ink-tertiary">
                          <Lock className="h-3 w-3" />
                          Upgrade
                        </span>
                      )}
                      {unlocked && (
                        <span className="ml-auto text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
                          On plan
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-ink-secondary">{body}</p>
                  </Link>
                ))}
              </div>
              <div className="flex flex-wrap gap-2 border-t border-border pt-4">
                <Link
                  to="/vendor/automations"
                  className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-3 py-1.5 text-sm font-medium text-ink hover:bg-surface-muted"
                >
                  <Workflow className="h-3.5 w-3.5" />
                  Automations
                  {!caps.can_automations && <Lock className="h-3 w-3 opacity-70" />}
                </Link>
                <Link
                  to="/vendor/calendar"
                  className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-3 py-1.5 text-sm font-medium text-ink hover:bg-surface-muted"
                >
                  <Calendar className="h-3.5 w-3.5" />
                  Full calendar
                </Link>
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default VendorWorkspace;
